import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckTree, Container, Popover, Progress, Stack } from 'rsuite'
import { FileDownload } from '@rsuite/icons';
import { ValueType } from 'rsuite/esm/CheckTree/CheckTree'
import { TreeNode } from 'rsuite/esm/internals/Tree/types'
import useQuery from '@/client/utils/useQuery'
import ExportModel from '@/client/models/export'
import { PanelError } from '@/client/components/ErrorProvider'
import { Button } from '@/client/components/Controls'
import { DownloadIcon, ListIcon } from '@/client/config'
import Loading from '../../components/Loading'
import ViewData from './ViewData'
import { useLayout } from '@/client/components/Layout/LayoutManager'
import { useHotkeys } from 'react-hotkeys-hook'
import { MessageBox } from '../../components/MessageBox'
import { Capabilities, hasPermission } from '@/shared'
import project from '@client/stores/projectStore'
import useProgress from '@/client/utils/useProgress'

//console.log('Load ExportLists ...')

type ExporterProps = { tab: any }
type ExporterProgressProps = { treeNodes: any; jobId: string; setJobId: (jobId: string) => void; onFinish: () => void }
type ExporterMappingsProps = { treeNodes: any; onCancel: () => void }
type CountDownProps = { counter: number }

const exportProgressContentStyle = {
    height: '100%',
}
const exportProgressStyle = {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginBottom: '1em',
}

const exportModel = new ExportModel()

const cancelMessage = 'Press ESC key to cancel'
const cancelHotkey = 'escape'

function ExportProgress({ treeNodes, jobId, setJobId, onFinish }: ExporterProgressProps) {
    const { t } = useTranslation()
    // The task record carries the id of the queue job, so the id this view
    // already has is the handle. What used to be a socket listener of its own
    // is now the store every progress display reads from.
    const { task, failed, error } = useProgress(jobId)

    // calculate nodes to export
    const calcedTreeNodes = useMemo(() => {
        return treeNodes.selected.map((selected: string) => {
            const [outTemplateId, fileName, sheetName, outTableId, outTableName] = selected.split('|')
            const node = treeNodes.items.find((item: any) => item.value === fileName)
            const exportData = node.exportData.filter((item: any) => item.fileName === sheetName)
            return { fileName, sheetName, outTableId, exportData, outTemplateId, outTableName }
        })
    }, [])

    // start job — once. StrictMode runs this effect twice in development, and
    // two jobs on the same template wrote over each other's files: the
    // download could come out as a zip without a workbook. The ref survives
    // StrictMode's remount; the conversion guards itself the same way.
    const startedRef = useRef(false)
    useEffect(() => {
        if (startedRef.current) return
        startedRef.current = true
        const _start = async () => {
            const res = await exportModel.export({
                project: project.currentProject.value?._id || '',
                data: calcedTreeNodes,
            })
            setJobId(res?.data?.jobId || '')
        }
        if (calcedTreeNodes?.length > 0) _start()
    }, [calcedTreeNodes])

    if (failed) {
        return (
            <Container className="center-in-parent">
                <span style={{ marginBottom: '2em' }}>{t('Export failed')}</span>
                <span style={{ marginBottom: '2em' }}>{error}</span>
                <Button appearance="ghost" onClick={onFinish}>
                    {t('Close')}
                </Button>
            </Container>
        )
    }

    if (task?.state === 'finished') {
        const downloadUrl = (task.result as any)?.downloadUrl

        return (
            <Container className="center-in-parent">
                <span style={{ marginBottom: '2em' }}>{t('Export finished')}</span>
                {downloadUrl && (
                    <Container style={{ marginBottom: '2em' }}>
                        <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <Button appearance="primary" size="lg">
                                <Stack spacing={8} alignItems="center">
                                    <FileDownload style={{ fontSize: 20 }} />
                                    <span>{t('Download Excel File')}</span>
                                </Stack>
                            </Button>
                        </a>
                    </Container>
                )}
                <Button appearance="ghost" onClick={onFinish}>
                    {t('Close')}
                </Button>
            </Container>
        )
    }

    return (
        <Container style={exportProgressContentStyle}>
            <Container style={exportProgressStyle}>
                {task?.message || t('Initializing')} ...
            </Container>
            <Progress
                percent={+(task?.progress ?? 0).toFixed(0)}
                strokeColor="var(--special-color-1)"
                trailColor="var(--color-2)"
                showInfo={true}
            />
            <div className="cancel-message">{t(cancelMessage)}</div>
        </Container>
    )
}

function ExportMappings({ treeNodes, onCancel }: ExporterMappingsProps) {
    const { t } = useTranslation()
    const [started, setStarted] = useState<boolean>(false)
    const [canceled, setCanceled] = useState<boolean>(false)
    const [counter, setCounter] = useState<number>(5)
    const [jobId, setJobId] = useState<string>('')

    // add escape key to cancel mapping
    useHotkeys(cancelHotkey, () => {
        setCanceled(true)
    })

    // decrease down counter
    useEffect(() => {
        if (counter > 0) {
            const timer = setTimeout(() => {
                if (!canceled) {
                    const nextCounter = counter - 1
                    if (nextCounter == 0) setStarted(true)
                    setCounter(nextCounter)
                }
            }, 1000)

            return () => clearTimeout(timer)
        }
    }, [counter, canceled])

    // job canceled
    const cancelJob = async () => {
        if (jobId) {
            const res = await exportModel.cancel({
                project: project.currentProject.value?._id || '',
                jobId
            })
            setJobId('')
        }
    }

    // handle yes and no button
    const handleYesNoButton = (status: string) => {
        setCanceled(false)
        if (status == 'yes') {
            setStarted(false)
            onCancel()
            cancelJob()
        }
    }

    // messagebox buttons
    const buttons = [
        { key: 'yes', text: t('Yes') },
        { key: 'no', text: t('No') },
    ]

    // the countdown dialog
    const CountDown = ({ counter }: CountDownProps) => {
        const { t } = useTranslation()
        return (
            <div className="countdown center-in-parent">
                <div>{t('Mapping is starting in {seconds} seconds ...'.replace('{seconds}', `${counter}`))}</div>
                <div>{t(cancelMessage)}</div>
            </div>
        )
    }

    return (
        <>
            <Popover className="mapping-converter-popover overlay" visible>
                {!started ? (
                    <CountDown counter={counter} />
                ) : (
                    <ExportProgress treeNodes={treeNodes} jobId={jobId} setJobId={setJobId} onFinish={onCancel} />
                )}
            </Popover>
            {canceled && (
                <MessageBox
                    message={t('Do you really want to cancel the mapping process?')}
                    buttons={buttons}
                    onClick={handleYesNoButton}
                />
            )}
        </>
    )
}

export default function Exporter({ tab }: ExporterProps) {
    const { t } = useTranslation()
    const { addTab } = useLayout()
    const [disableStartButton, setDisableStartButton] = useState<boolean>(() => {
        return tab?.store?.treeNodes?.selected?.length == 0 || true
    })
    const [exportingStarted, setExportingStarted] = useState<boolean>(false)
    const { loading, error, data } = useQuery(exportModel.list, {
        args: { project: project.currentProject.value?._id, page: 1, count: 200 },
    })

    /**
     * fetch all valid out tables from this project
     *
     * The result has to be like this example
     * ├─ Debitoren - template - debitoren.xlxs     <- out table
     * │   ├─ 2025-05-01 - debitoren                <- in table
     * │   │   ┖- Tabelle1 - 123 items              <- sheet name of in table
     * │   ┖─ 2025-06-01 - debitoren
     * │       ┖- Tabelle1 - 98 items
     * │
     * ├─ next template ...
     */
    const treeNodes = useMemo(() => {
        function hasChildren(item: any) {
            if (!item.sheets?.length) return false

            let result = false
            item.sheets.forEach((sheet: any) => {
                if (sheet.inTableList?.length) {
                    result = true
                    return false
                }
            })
            return result
        }

        if (!data || tab?.store?.treeNodes) {
            setDisableStartButton(tab?.store?.treeNodes?.selected?.length == 0)
            return tab?.store?.treeNodes || { items: [], selected: [] }
        }

        const selected: any[] = []
        const uncheckable: any[] = []
        const items: any[] = []
        data && data?.forEach((item: any) => {
            const node: any = {
                label: `${item.name} - ${item.excel}`,
                value: item.excel,
                children: [],
                outTemplateId: item.outTemplateId,
            }

            const exportData: any[] = [] // outTableId, fileName, sheetName, inTableId, fileId
            item.sheets?.forEach((sheet: any, sheetIndex: number) => {
                sheet.inTableList?.forEach((inTable: any, tableIndex: number) => {
                    exportData.push({
                        outTableId: sheet.outTableId,
                        fileName: inTable.fileName,
                        //sheetName: inTable.sheetName,
                        sheetName: sheet.name,
                        inTableId: inTable.inTableId,
                        fileId: inTable.fileId,
                    })

                    //const childNodeValue = `${item.outTemplateId}|${item.excel}|${inTable.fileName}|${sheet.outTableId}|${sheet.name}`
                    const childNodeValue = `${item.outTemplateId}|${item.excel}|${inTable.fileName}`
                    let childNode = node.children.find((child: any) => child.value === childNodeValue)
                    if (!childNode) {
                        childNode = {
                            selectable: false,
                            outTableId: sheet.outTableId,
                            totalRows: sheet.totalRows,
                            label: inTable.fileName,
                            value: childNodeValue,
                            parent: node,
                        }
                        node.children.push(childNode)
                        if (!hasChildren(item)) uncheckable.push(childNodeValue)
                        else childNode.children = []
                        childNode.outTableId = sheet.outTableId
                        childNode.totalRows = sheet.totalRows
                    }

                    const strRows = t('rowItems', { count: inTable.rows })
                    const value = `${item.outTemplateId}|${inTable.fileName}|${inTable.sheetName}|${sheet.outTableId}|${inTable.inTableId}|${inTable.fileId}`
                    childNode.children.push({
                        selectable: true,
                        outTableId: sheet.outTableId,
                        inTableId: inTable.inTableId,
                        fileId: inTable.fileId,
                        fileName: inTable.fileName,
                        sheetName: inTable.sheetName,
                        rows: inTable.rows,
                        label: `${inTable.sheetName} - ${inTable.rows} ${strRows}`,
                        value: value,
                        parent: childNode,
                    } as never)
                    childNode.selectable = true
                    uncheckable.push(value)
                })
            })

            node.exportData = exportData
            // One data state per template (operator, 13.10.2026): with more
            // than one, ticking the template as a whole would mean all of them.
            if (node.children.length > 1) uncheckable.push(node.value)
            items.push(node as never)
        })
        //console.log('treeNodes: ', {items, selected})
        tab.store.treeNodes = { items, selected, uncheckable }
        setDisableStartButton(selected.length == 0)
        return tab.store.treeNodes
    }, [data])
    const nodeRef = useRef(undefined)

    // Either or: once a data state of a template is ticked, the template's
    // other data states are out of reach until it is unticked again.
    const [selectionChanged, setSelectionChanged] = useState<number>(0)
    const uncheckable = useMemo(() => {
        const blocked: string[] = []
        const selected: string[] = treeNodes?.selected ?? []
        for (const template of treeNodes?.items ?? []) {
            const files: any[] = template.children ?? []
            const chosen = files.find((file) => selected.includes(file.value))
            if (chosen) blocked.push(...files.filter((file) => file !== chosen).map((file) => file.value))
        }
        return [...(treeNodes?.uncheckable ?? []), ...blocked]
    }, [treeNodes, selectionChanged])

    if (error) return <PanelError message={error.message} />
    if (loading) return <Loading />

    // on click save checkbox state immediately to database
    //
    // What is selected is a data state: the file node of a template. A
    // template counts as its one data state; with more it cannot be ticked
    // (see uncheckable). The file node's value is the form the export reads —
    // ticking a file used to store its sheets' values, which it did not.
    function onSelect(item: any, value: ValueType, event: any) {
        const selected: string[] = []
        const checked = item.check
        if (!item.parent) {
            if (item.children?.length === 1) selected.push(item.children[0].value)
        } else if (item.children) {
            selected.push(item.value)
        }

        if (checked) {
            // add checked
            tab.store.treeNodes.selected = [...tab.store.treeNodes.selected, ...selected]
        } else {
            // remove checked
            tab.store.treeNodes.selected = tab.store.treeNodes.selected.filter((item: string) => {
                return selected.indexOf(item) == -1
            })
        }

        setDisableStartButton(tab.store.treeNodes.selected.length == 0)
        setSelectionChanged((n) => n + 1)
        // inTableColumnModel.updateUseOnMapping(project.currentProject._id, {
        //     ids: list,
        //     checked: checked,
        // })
    }

    // start export
    function onStartExporter(nodeData: any, e: any): void {
        e.preventDefault()
        setExportingStarted(true)
        nodeRef.current = nodeData ? { items: treeNodes.items, selected: [nodeData.value], uncheckable: [] } : treeNodes
    }

    const viewController = {
        getTabContent(params: any) {
            const { activeKey, tab } = params
            return <ViewData tab={tab} />
        },
        getBreadCrumb(params: any) {
            const { tab } = params
            const breadCrumb: string[] = [t('Exporter'), t('View')]
            const path = tab.store.data.parent.value.split('|')
            breadCrumb.push(`${path[1]} (${path[2]})`)
            //breadCrumb.push(`${tab.store.data.fileName}::${tab.store.data.sheetName}`)
            return breadCrumb
        },
    }

    function onView(nodeData: any, e: any) {
        e.preventDefault()
        addTab({
            dataId: `export-view-data-${nodeData.inTableId}`,
            name: t('Exported {name}').replace('{name}', nodeData.sheetName),
            component: 'export',
            controller: viewController,
            icon: <ListIcon />,
            store: {
                data: nodeData,
            },
        })
    }

    // cancel export
    function onCancel() {
        setExportingStarted(false)
    }

    const ExporterTop = () => {
        return (
            <Container className="mapping-converter-top">
                <span>&nbsp;</span>
                {hasPermission(Capabilities.export_write) && (
                    <Button appearance="ghost" disabled={disableStartButton} onClick={(e) => onStartExporter(null, e)}>
                        {t('Start export')}
                    </Button>
                )}
            </Container>
        )
    }

    function renderTreeNode(nodeData: TreeNode): ReactNode {
        if (!nodeData.selectable) return nodeData.label

        const ignoreClick = (e: any) => {
            if (!e.target.hasChildNodes()) e.preventDefault()
            //e.stopPropagation()
            console.log(e.target)
        }

        return (
            <div className="treenode-item" >
                <span onClick={(e) => { ignoreClick(e) }}>{nodeData.label}</span>
                <div className="treenode-item-actions">
                    {nodeData.children?.length ? (
                        hasPermission(Capabilities.export_write) ? (
                            <button onClick={(e) => onStartExporter(nodeData, e)} title={t('Export data')}>
                                <DownloadIcon />
                            </button>
                        ) : null
                    ) : (
                        <button onClick={(e) => onView(nodeData, e)} title={t('View data')}>
                            <ListIcon />
                        </button>
                    )}
                </div>
            </div>
        )
    }

    return (
        <>
            <Container className="mapping-converter">
                <ExporterTop />
                <Container className="mapping-converter-content">
                    <CheckTree
                        defaultExpandAll
                        defaultValue={treeNodes?.selected}
                        uncheckableItemValues={uncheckable}
                        data={treeNodes?.items}
                        onSelect={onSelect}
                        renderTreeNode={renderTreeNode}
                    />
                </Container>
            </Container>
            {exportingStarted && <ExportMappings treeNodes={nodeRef.current} onCancel={onCancel} />}
        </>
    )
}
